import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import {
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SellIcon from '@mui/icons-material/Sell';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import CancelIcon from '@mui/icons-material/Cancel';
import { useMaterialUIController } from "context";
import { useGlobalMDAlert } from "context";
import { puedeAccion } from "lib/seguridad/permisosPorModulo";
import { useApi } from "lib/api";
import { FP, PageHeader, fpDataGridSx, fpInputSx, StatusPill } from "components/financepro";

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2.5,
        border: `1px solid ${FP.color.border}`,
        bgcolor: FP.color.cardBg,
        boxShadow: FP.shadow.soft,
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: accent,
        },
      }}
    >
      <CardContent sx={{ p: 2.4, '&:last-child': { pb: 2.4 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
          <Box flex={1} minWidth={0}>
            <Typography
              sx={{
                color: FP.color.textSecondary,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontSize: '0.66rem',
                mb: 1,
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                color: FP.color.textPrimary,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                fontSize: '1.4rem',
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              width: 42,
              height: 42,
              borderRadius: 1.4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${accent}14`,
              color: accent,
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function esVendido(estado) {
  return String(estado ?? '').trim().toUpperCase() === 'V';
}

function esDevuelto(estado) {
  return String(estado ?? '').trim().toUpperCase() === 'D';
}

function esDisponible(estado) {
  const s = String(estado ?? '').trim().toUpperCase();
  return s === '' || s === 'A';
}

function estadoPill(estado) {
  const s = String(estado ?? '').trim().toUpperCase();
  if (s === 'V') return <StatusPill label="Vendido" variant="neutral" />;
  if (s === 'D') return <StatusPill label="Devuelto" variant="danger" />;
  if (esDisponible(s)) return <StatusPill label="Disponible" variant="success" />;
  return <StatusPill label={estado || '—'} variant="info" />;
}

function InformeConsignaciones({ embedded = false, refreshToken = 0 } = {}) {
  const [facturas, setFacturas] = useState([])
  const [facturaSel, setFacturaSel] = useState(null)
  const [rows, setRows] = useState([])
  const [filtroEstado, setFiltroEstado] = useState('todos')
  const [busquedaChasis, setBusquedaChasis] = useState('')
  const [confirmRow, setConfirmRow] = useState(null)
  const [anulando, setAnulando] = useState(false)

  const [controller] = useMaterialUIController()
  const { roleDetalles } = controller
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const puedeDevolver = true//puedeAccion(roleDetalles, "motores", "borrar")

  async function cargarFacturas() {
    const { status, data } = await post('/api/listarfacturasconsignadas')
    if (status === 200) {
      setFacturas(data["facturas"] || [])
    } else {
      setFacturas([])
      showAlert({ message: data?.Error || "No se pudieron cargar las facturas consignadas", color: "error", autoHideDuration: 4000 })
    }
  }

  useEffect(() => {
    cargarFacturas()
  }, [post, refreshToken]);

  async function cargarMotores(nfactura) {
    const { status, data } = await post('/api/consignacionesporfactura', { nfactura })
    if (status === 200) {
      setRows(data["consignaciones"] || [])
    }
    if (status === 400) {
      setRows([])
      showAlert({ message: data["Error"], color: "error", autoHideDuration: 3000 })
    }
  }

  function onSelectFactura(value) {
    setFacturaSel(value)
    setFiltroEstado('todos')
    setBusquedaChasis('')
    if (value && value.nfactura) {
      cargarMotores(value.nfactura)
    } else {
      setRows([])
    }
  }

  async function confirmarDevolucion() {
    if (!confirmRow) return
    setAnulando(true)
    try {
      const { status, data } = await post('/api/anularconsignacion', { chasis: confirmRow.chasis, nfactura: confirmRow.nfactura })
      if (status === 200) {
        setRows((prev) => prev.filter((r) => r.chasis !== confirmRow.chasis))
        setAnulando(false)
        setConfirmRow(null)
        showAlert({ message: "Consignación devuelta correctamente", color: "success", autoHideDuration: 3000 })
        cargarFacturas() // refresca el total de la factura en el selector
      } else {
        setAnulando(false)
        showAlert({ message: data?.Error || "No se pudo devolver la consignación", color: "error", autoHideDuration: 4000 })
      }
    } catch (err) {
      setAnulando(false)
      showAlert({ message: "Error de red: " + err.message, color: "error", autoHideDuration: 4000 })
    }
  }

  const accionDevolver = (row) => {
    if (esDisponible(row.estado) && puedeDevolver) {
      return (
        <Tooltip title="Devolver / anular consignación">
          <IconButton size="small" onClick={() => setConfirmRow(row)} sx={{ color: FP.color.danger }}>
            <AssignmentReturnIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    }
    return <Typography sx={{ color: FP.color.textMuted, fontSize: '0.72rem', fontWeight: 700 }}>—</Typography>
  }

  const columns = useMemo(() => [
    { field: 'fecha', headerName: 'Fecha', minWidth: 100, flex: 0.6 },
    { field: 'chasis', headerName: 'Chasis', minWidth: 150, flex: 1 },
    { field: 'marca', headerName: 'Marca', minWidth: 110, flex: 0.7 },
    { field: 'modelo', headerName: 'Modelo', minWidth: 110, flex: 0.7 },
    { field: 'colore', headerName: 'Color', minWidth: 100, flex: 0.6 },
    { field: 'sucursal', headerName: 'Sucursal', minWidth: 120, flex: 0.7 },
    { field: 'costo', headerName: 'Costo', minWidth: 100, flex: 0.6 },
    { field: 'precio', headerName: 'Precio', minWidth: 100, flex: 0.6 },
    {
      field: 'estado',
      headerName: 'Estado',
      minWidth: 120,
      flex: 0.6,
      renderCell: (params) => estadoPill(params.value),
    },
    {
      field: '__devolver',
      headerName: 'Acción',
      width: 90,
      minWidth: 90,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => accionDevolver(params.row),
    },
  ], [puedeDevolver]);

  const parseN = (v) => parseFloat(String(v ?? '0').split(',').join('')) || 0;
  const totalCosto = rows.reduce((acc, r) => acc + parseN(r.costo), 0);
  const disponibles = rows.filter((r) => esDisponible(r.estado)).length;
  const devueltos = rows.filter((r) => esDevuelto(r.estado)).length;

  const filasVista = useMemo(() => {
    const q = busquedaChasis.trim().toLowerCase();
    return rows.filter((r) => {
      let okEstado = true;
      if (filtroEstado === 'disponibles') okEstado = esDisponible(r.estado);
      else if (filtroEstado === 'vendidos') okEstado = esVendido(r.estado);
      else if (filtroEstado === 'devueltos') okEstado = esDevuelto(r.estado);
      const okChasis = !q || String(r.chasis ?? '').toLowerCase().includes(q);
      return okEstado && okChasis;
    });
  }, [rows, filtroEstado, busquedaChasis]);

  const contenido = (
    <>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'visible',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ReceiptLongIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Seleccione la factura consignada
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={7}>
                <Autocomplete
                  options={facturas || []}
                  value={facturaSel}
                  getOptionLabel={(option) =>
                    option ? `Factura ${option.nfactura} · ${option.consignante || 'S/N'}` : ''
                  }
                  isOptionEqualToValue={(option, value) =>
                    String(option?.nfactura) === String(value?.nfactura)
                    && String(option?.suplidor) === String(value?.suplidor)
                  }
                  onChange={(e, value) => onSelectFactura(value || null)}
                  noOptionsText={facturas.length === 0 ? 'No hay facturas consignadas' : 'Sin coincidencias'}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" label="Factura" fullWidth InputLabelProps={{ shrink: true }} sx={fpInputSx} />
                  )}
                  fullWidth
                />
              </Grid>
              {facturaSel && (
                <Grid item xs={12} md={5}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Consignante · Fecha · Total factura
                    </Typography>
                    <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.95rem', fontWeight: 700 }}>
                      {[facturaSel.consignante, facturaSel.fecha, `$${facturaSel.valor}`].filter(Boolean).join('  ·  ')}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              width: '100%',
              gap: 2,
              mb: 3,
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
            }}
          >
            <StatCard label="Motores en factura" value={String(rows.length)} icon={DescriptionIcon} accent={FP.color.accentBlue} />
            <StatCard label="Disponibles" value={String(disponibles)} icon={SellIcon} accent={FP.color.successDark} />
            <StatCard label="Devueltos" value={String(devueltos)} icon={AssignmentReturnIcon} accent={FP.color.danger} />
            <StatCard label="Total costo" value={`$${totalCosto.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} icon={AttachMoneyIcon} accent={FP.color.navy} />
          </Box>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Motores de la factura
            </Typography>
            {rows.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Buscar chasis"
                  value={busquedaChasis}
                  onChange={(e) => setBusquedaChasis(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ ...fpInputSx, minWidth: { xs: '100%', sm: 220 } }}
                />
                <FormControl size="small" sx={{ ...fpInputSx, minWidth: 170 }}>
                  <InputLabel id="filtro-estado-cons-label" shrink>Estado</InputLabel>
                  <Select
                    labelId="filtro-estado-cons-label"
                    label="Estado"
                    notched
                    value={filtroEstado}
                    onChange={(e) => setFiltroEstado(e.target.value)}
                  >
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="disponibles">Disponibles</MenuItem>
                    <MenuItem value="vendidos">Vendidos</MenuItem>
                    <MenuItem value="devueltos">Devueltos</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {filasVista.length > 0 ? (
              <>
                <Box sx={{ display: { xs: 'none', md: 'block' }, width: '100%', overflowX: 'auto' }}>
                  <DataGrid
                    rows={filasVista}
                    columns={columns}
                    getRowId={(row) => (row.id != null ? String(row.id) : String(row.chasis))}
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    disableRowSelectionOnClick
                    rowHeight={42}
                    sx={{ minWidth: { xs: 720, sm: '100%' }, height: 520, ...fpDataGridSx }}
                    autoHeight={false}
                  />
                </Box>
                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.4 }}>
                  {filasVista.map((row, idx) => (
                    <Box
                      key={row.id != null ? String(row.id) : `${row.chasis}-${idx}`}
                      sx={{
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: '#FFFFFF',
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '0.95rem', lineHeight: 1.2, wordBreak: 'break-word' }}>
                            {row.chasis}
                          </Typography>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', mt: 0.25 }}>
                            {[row.marca, row.modelo, row.colore].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                          {estadoPill(row.estado)}
                          {esDisponible(row.estado) && puedeDevolver && (
                            <IconButton size="small" onClick={() => setConfirmRow(row)} sx={{ color: FP.color.danger }}>
                              <AssignmentReturnIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 1 }}>
                        {[
                          ['Sucursal', row.sucursal],
                          ['Costo', row.costo],
                          ['Precio', row.precio],
                          ['Fecha', row.fecha],
                        ].map(([label, value]) => (
                          <Box key={label}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                              {label}
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.9rem', fontWeight: 600, wordBreak: 'break-word' }}>
                              {value ?? '—'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  {!facturaSel
                    ? 'No hay datos para mostrar'
                    : rows.length === 0
                      ? 'La factura no tiene motores consignados'
                      : 'Ningún motor coincide con el filtro o la búsqueda'}
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  {rows.length > 0
                    ? 'Cambie el estado o el texto de búsqueda del chasis.'
                    : 'Seleccione una factura para ver sus motores y devolver los que no estén vendidos.'}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <Dialog
          open={!!confirmRow}
          onClose={() => (anulando ? null : setConfirmRow(null))}
          maxWidth="xs"
          fullWidth
          fullScreen={isXs}
          PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={800}>Devolver consignación</Typography>
            <IconButton onClick={() => setConfirmRow(null)} disabled={anulando} sx={{ p: 1 }}>
              <CancelIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem' }}>
              ¿Seguro que deseas devolver y anular esta consignación? El motor
              {confirmRow ? ` (chasis ${confirmRow.chasis})` : ''} se eliminará del inventario, su línea de cuenta por pagar quedará anulada y se descontará su valor del total de la factura. Esta acción no se puede deshacer.
            </Typography>
            <DialogActions sx={{ mt: 2, px: 0 }}>
              <Button
                variant="outlined"
                onClick={() => setConfirmRow(null)}
                disabled={anulando}
                sx={{
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: FP.color.textPrimary,
                  borderColor: FP.color.borderStrong,
                  '&:hover': { borderColor: FP.color.textSecondary, bgcolor: FP.color.surface },
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={<AssignmentReturnIcon />}
                onClick={confirmarDevolucion}
                disabled={anulando}
                sx={{
                  bgcolor: FP.color.danger,
                  color: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.danger, filter: 'brightness(0.92)' },
                }}
              >
                {anulando ? 'Devolviendo…' : 'Devolver'}
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
    </>
  );

  if (embedded) {
    return <Box>{contenido}</Box>;
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader title="Consignaciones" subtitle="Devolución de motores por factura" />
      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>{contenido}</Box>
    </Box>
  );
}

export default InformeConsignaciones
